import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { chatJobsKey } from "~/entities/chat/useActiveChat";
import { env } from "~/env";
import { getAccessToken } from "~/shared/lib/auth/auth-client";
import { runSSE } from "~/shared/lib/sse-reader";
import type { JobStatus } from "~/shared/types/chats";

export interface GenerationStreamState {
    /** True пока активный SSE-стрим открыт. Используется как сигнал
     * для polling-fallback, чтобы тот включался **только** когда SSE
     * реально мёртв и не дублировал трафик. */
    isConnected: boolean;
}

// Текущий backend-payload (из pg_notify_helper): один общий тип
// события «job updated», discriminate'имся по `job.status`.
// Парсер `parseGenerationStreamEvent` валидирует payload и тихо
// дропает мусор (null, обрезанный JSON, неизвестная форма) —
// без лишних invalidate'ов.
export type GenerationStreamJobStatus = JobStatus;

export interface GenerationStreamJobUpdated {
    chat_id: string;
    chat_uuid: string;
    message_id: string;
    job: {
        id: string;
        status: GenerationStreamJobStatus;
        message_id: string;
        model_name: string | null;
        error_message?: string | null;
    };
}

export type GenerationStreamEvent = GenerationStreamJobUpdated;

function isJobUpdated(value: unknown): value is GenerationStreamJobUpdated {
    if (typeof value !== "object" || value === null) return false;
    const v = value as Record<string, unknown>;
    if (typeof v.chat_uuid !== "string") return false;
    if (typeof v.message_id !== "string") return false;
    const job = v.job;
    if (typeof job !== "object" || job === null) return false;
    const j = job as Record<string, unknown>;
    return typeof j.id === "string" && typeof j.status === "string";
}

// runSSE сам парсит JSON и передаёт `unknown`. Здесь только
// type-narrowing — на garbage возвращаем null, чтобы handler
// мог тихо проигнорировать неизвестное событие.
export function parseGenerationStreamEvent(value: unknown): GenerationStreamEvent | null {
    return isJobUpdated(value) ? value : null;
}

export function useGenerationStream(chatUuid: string | null): GenerationStreamState {
    const qc = useQueryClient();
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        if (!chatUuid) {
            setIsConnected(false);
            return;
        }
        const token = getAccessToken();
        if (!token) return;

        const controller = new AbortController();
        const url = `${env.VITE_API_URL}/v2/artifacts/generation-jobs/stream?chat_uuid=${encodeURIComponent(chatUuid)}`;

        // SSE-события касаются ТОЛЬКО джоб генераций. Список сообщений в
        // ленте от стрима не меняется (новые user-сообщения локально,
        // ассистентских bubbles мы сейчас не рендерим). Поэтому
        // chatMessagesKey тут НЕ инвалидируем — иначе каждый event /
        // reconnect / focus тащил бы лишний `messages?limit=100` запрос.
        const invalidateJobs = () => {
            void qc.invalidateQueries({ queryKey: chatJobsKey(chatUuid) });
        };

        const handleMessage = (value: unknown) => {
            // Парсим через типизированный union: сейчас один вариант
            // (job updated). Валидация защитит от garbage-payload —
            // без неё каждый «битый» SSE-event дёргал бы лишний
            // invalidate.
            const event = parseGenerationStreamEvent(value);
            if (!event) return;
            invalidateJobs();
        };

        void runSSE({
            url,
            token,
            signal: controller.signal,
            onMessage: handleMessage,
            onConnect: () => {
                setIsConnected(true);
                // catch-up на каждый успешный (re)connect — ловим события,
                // которые могли уйти в окно между потерей соединения и
                // переподключением.
                invalidateJobs();
            },
            onDisconnect: () => setIsConnected(false),
        });

        const onVisible = () => {
            if (document.visibilityState === "visible") {
                invalidateJobs();
            }
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            controller.abort();
            document.removeEventListener("visibilitychange", onVisible);
            setIsConnected(false);
        };
    }, [chatUuid, qc]);

    return { isConnected };
}
